import AppContext from "PFApp/app_context";
import { Button } from "PFComponents/button";
import ButtonLink from "PFComponents/button/button_link";
import PageCard from "PFComponents/page/page_card";
import Toggle from "PFComponents/toggle/toggle";
import { GROWL_ACTIONS } from "PFCore/reducers/growl_reducer";
import PropTypes from "prop-types";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { humanize } from "underscore.string";

import SaasAdminHeader from "../shared/saas_admin_header";
import css from "./cv_parsing_conf_page.less";

const OptionToggleButton = ({ name, tip, value, handleChange, customTypes, overrideName, overrideValue }) => {
  const ct = customTypes.find((customType) => customType.name === name);
  const displayAs = (ct && ct["display_as"]) || humanize(name);

  return (
    <tr style={{ borderTop: "20px solid #fff", borderBottom: "20px solid #fff", padding: "0 10px" }}>
      <td style={{ verticalAlign: "middle" }}>
        <Toggle
          checked={value}
          qaId={`Toggle-${name}`}
          label={<div>{displayAs}</div>}
          onChange={(val) => handleChange(name, val)}
        />
      </td>
      <td style={{ opacity: 0.6, verticalAlign: "middle", textAlign: "left" }}>{tip}</td>
      <td style={{ verticalAlign: "middle" }}>
        {overrideName && value && (
          <Toggle
            checked={overrideValue}
            qaId={`Toggle-override-${name}`}
            label={<div>{`Override Existing ${displayAs}`}</div>}
            onChange={(val) => handleChange(overrideName, val)}
          />
        )}
      </td>
    </tr>
  );
};

OptionToggleButton.propTypes = {
  name: PropTypes.string,
  tip: PropTypes.string,
  value: PropTypes.bool,
  handleChange: PropTypes.func,
  handleDependencies: PropTypes.func,
  customTypes: PropTypes.array,
  overrideName: PropTypes.string,
  overrideValue: PropTypes.bool
};

const CvParsingConfPage = ({ cvParserConfig, customTypes, accountName, putCvParsingConfig }) => {
  const { t } = useTranslation("translation");
  const history = useHistory();
  const [formData, setFormData] = useState(cvParserConfig);
  const { dispatch } = useContext(AppContext);
  const type = GROWL_ACTIONS.GROWL_RENDER;

  const handleFormValueUpdate = (name, value) => {
    const updatedFormData = { ...formData };
    updatedFormData[name] = value;
    setFormData(updatedFormData);
  };

  const handleChangeWithDependency = (name, value, handleDependency) => {
    const updatedFormData = { ...formData };
    updatedFormData[name] = value;
    handleDependency(updatedFormData);
    setFormData(updatedFormData);
  };

  const handleSubmit = () => {
    putCvParsingConfig(formData)
      .then(() => {
        dispatch({
          type,
          payload: { message: "CV Parsing Configuration has been saved", kind: "success" }
        });
        history.push("/saas_admin/accounts");
      })
      .catch((err) => {
        dispatch({
          type,
          payload: { message: `CV Parsing Configuration failed: ${err}`, kind: "error" }
        });
      });
  };

  return (
    <div className={css.cvParsingConfigPage}>
      <SaasAdminHeader title="CV Parsing Configuration" subTitle={`(for ${accountName})`} />
      <PageCard
        style={{
          padding: "4px 12px",
          marginTop: "20px"
        }}
      >
        <table>
          <tbody>
            <OptionToggleButton
              name="enabled"
              handleChange={handleFormValueUpdate}
              customTypes={customTypes}
              value={formData["enabled"]}
              tip={
                <>
                  When set to <b>true</b> the user may upload their CV on the first page of the registration
                  process or by uploading through the <b>Upload Your CV</b> button in the edit profile
                  navigation bar.
                </>
              }
            />
            {formData["enabled"] && (
              <>
                <OptionToggleButton
                  name="experience"
                  handleChange={(name, val) =>
                    handleChangeWithDependency(name, val, (formData) => {
                      !val && (formData["current_position"] = false);
                      return formData;
                    })
                  }
                  handleDependencies={(val) => !val && handleFormValueUpdate("current_position", false)}
                  customTypes={customTypes}
                  value={formData["experience"]}
                  tip={
                    <>
                      When set to <b>true</b> previous positions data are imported from the CV
                    </>
                  }
                />
                <OptionToggleButton
                  name="internal_experience"
                  handleChange={handleFormValueUpdate}
                  customTypes={customTypes}
                  value={formData["internal_experience"]}
                  tip={
                    <>
                      When set to <b>true</b> internal experiences data are imported from the CV
                    </>
                  }
                />
                <OptionToggleButton
                  name="current_position"
                  handleChange={(name, val) =>
                    handleChangeWithDependency(name, val, (formData) => {
                      val && (formData["experience"] = true);
                      return formData;
                    })
                  }
                  customTypes={customTypes}
                  value={formData["current_position"]}
                  tip={
                    <>
                      When set to <b>true</b> the user may upload main positions without <b>end_date</b> in
                      past experience.
                    </>
                  }
                />
                <OptionToggleButton
                  name="name"
                  handleChange={handleFormValueUpdate}
                  customTypes={customTypes}
                  value={formData["name"]}
                  tip={
                    <>
                      When set to <b>true</b> the user may upload their First and Last Names from CV.
                    </>
                  }
                  overrideName="override_name"
                  overrideValue={formData["override_name"]}
                />
                <OptionToggleButton
                  name="summary"
                  handleChange={handleFormValueUpdate}
                  customTypes={customTypes}
                  value={formData["summary"]}
                  tip={
                    <>
                      When set to <b>true</b> the user may upload their Short Bio from CV.
                    </>
                  }
                  overrideName="override_summary"
                  overrideValue={formData["override_summary"]}
                />
                <OptionToggleButton
                  name="brands"
                  handleChange={handleFormValueUpdate}
                  customTypes={customTypes}
                  value={formData["brands"]}
                  tip={
                    <>
                      When set to <b>true</b> the user may upload their brands data from CV.
                    </>
                  }
                  overrideName="override_brands"
                  overrideValue={formData["override_brands"]}
                />
                <OptionToggleButton
                  name="qualifications"
                  handleChange={handleFormValueUpdate}
                  customTypes={customTypes}
                  value={formData["qualifications"]}
                  tip={
                    <>
                      When set to <b>true</b> the user may upload their qualifications data from CV.
                    </>
                  }
                  overrideName="override_qualifications"
                  overrideValue={formData["override_qualifications"]}
                />
                <OptionToggleButton
                  name="languages"
                  handleChange={handleFormValueUpdate}
                  customTypes={customTypes}
                  value={formData["languages"]}
                  tip={
                    <>
                      When set to <b>true</b> the user may upload their languages data from CV.
                    </>
                  }
                  overrideName="override_languages"
                  overrideValue={formData["override_languages"]}
                />
                <OptionToggleButton
                  name="skills"
                  handleChange={handleFormValueUpdate}
                  customTypes={customTypes}
                  value={formData["skills"]}
                  tip={
                    <>
                      When set to <b>true</b> the user may upload their skills data from CV.
                    </>
                  }
                  overrideName="override_skills"
                  overrideValue={formData["override_skills"]}
                />
                <OptionToggleButton
                  name="memberships"
                  handleChange={handleFormValueUpdate}
                  customTypes={customTypes}
                  value={formData["memberships"]}
                  tip={
                    <>
                      When set to <b>true</b> the user may upload their memberships data from CV.
                    </>
                  }
                  overrideName="override_memberships"
                  overrideValue={formData["override_memberships"]}
                />
                <OptionToggleButton
                  name="certificates"
                  handleChange={handleFormValueUpdate}
                  customTypes={customTypes}
                  value={formData["certificates"]}
                  tip={
                    <>
                      When set to <b>true</b> the user may upload their certificates data from CV.
                    </>
                  }
                  overrideName="override_certificates"
                  overrideValue={formData["override_certificates"]}
                />
                <OptionToggleButton
                  name="accreditations"
                  handleChange={handleFormValueUpdate}
                  customTypes={customTypes}
                  value={formData["accreditations"]}
                  tip={
                    <>
                      When set to <b>true</b> the user may upload their accreditations data from CV.
                    </>
                  }
                  overrideName="override_accreditations"
                  overrideValue={formData["override_accreditations"]}
                />
                <OptionToggleButton
                  name="sectors"
                  handleChange={handleFormValueUpdate}
                  customTypes={customTypes}
                  value={formData["sectors"]}
                  tip={
                    <>
                      When set to <b>true</b> the user may upload their sectors data from CV.
                    </>
                  }
                  overrideName="override_sectors"
                  overrideValue={formData["override_sectors"]}
                />
              </>
            )}
          </tbody>
        </table>
        <div className={css.cvParsingConfigActions}>
          <ButtonLink kind="secondary" href="/saas_admin/accounts">
            {t("cancel")}
          </ButtonLink>
          <Button text={t("save")} kind="primary" onClick={handleSubmit} />
        </div>
      </PageCard>
    </div>
  );
};

CvParsingConfPage.propTypes = {
  accountName: PropTypes.string,
  cvParserConfig: PropTypes.object,
  customTypes: PropTypes.array,
  putCvParsingConfig: PropTypes.func
};

export default CvParsingConfPage;
